/**
 * SetCard — generic wrapper for conditional-set inline cards.
 *
 * The cascade engine activates clinical sets based on captured data
 * (e.g. primary_impression contains "stroke" → activate the stroke
 * set, which surfaces a LAMS / last-known-well / Cincinnati card
 * inline at the assessment stage).
 *
 * Every card uses this wrapper so the user gets a consistent visual:
 *   - small "Triggered by" banner explaining WHY the card appeared
 *   - small "Feeds" footer listing the downstream consumers
 *   - body slot for the actual fields
 */

import type { ReactNode } from 'react';
import { Sparkles } from 'lucide-react';
import type { ConsumerId } from '@/lib/pipeline-graph';
import { CONSUMERS } from '@/lib/pipeline-graph';
import { cn } from '@/lib/cn';

const CONSUMER_CATEGORY_BG: Record<string, string> = {
  regulatory: 'bg-red-100 text-red-800 border-red-300',
  clinical:   'bg-emerald-100 text-emerald-800 border-emerald-300',
  department: 'bg-blue-100 text-blue-800 border-blue-300',
  financial:  'bg-amber-100 text-amber-800 border-amber-300',
};

interface Props {
  title: string;
  triggeredBy: string;
  feeds: ConsumerId[];
  /** Tailwind accent for the title bar — defaults to amber. */
  accent?: 'amber' | 'emerald' | 'rose' | 'blue';
  children: ReactNode;
}

const ACCENT_RING: Record<NonNullable<Props['accent']>, string> = {
  amber:   'border-amber-300',
  emerald: 'border-emerald-300',
  rose:    'border-rose-300',
  blue:    'border-blue-300',
};

const ACCENT_BANNER: Record<NonNullable<Props['accent']>, string> = {
  amber:   'border-amber-400 bg-amber-50 text-amber-900',
  emerald: 'border-emerald-400 bg-emerald-50 text-emerald-900',
  rose:    'border-rose-400 bg-rose-50 text-rose-900',
  blue:    'border-blue-400 bg-blue-50 text-blue-900',
};

export function SetCard({ title, triggeredBy, feeds, accent = 'amber', children }: Props) {
  return (
    <section className={cn('border bg-white rounded-lg overflow-hidden', ACCENT_RING[accent])}>
      <header className={cn('flex items-start gap-2 border-l-4 px-3 py-2', ACCENT_BANNER[accent])}>
        <Sparkles size={16} className="shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold">{title}</h3>
          <p className="text-[11px] mt-0.5 leading-snug">
            <strong>Triggered by:</strong> {triggeredBy}
          </p>
        </div>
      </header>

      <div className="p-3 space-y-3">{children}</div>

      {feeds.length > 0 && (
        <footer className="border-t border-slate-200 bg-slate-50 px-3 py-1.5 flex items-center gap-1.5 flex-wrap">
          <span className="text-[9px] font-mono uppercase tracking-widest text-slate-500">
            feeds {feeds.length} →
          </span>
          {feeds.map((id) => {
            const c = CONSUMERS[id];
            return (
              <span
                key={id}
                title={`${c.name} — ${c.rationale}`}
                className={cn(
                  'inline-block px-1.5 py-0.5 rounded text-[9px] font-mono font-bold border',
                  CONSUMER_CATEGORY_BG[c.category],
                )}
              >
                {c.short}
              </span>
            );
          })}
        </footer>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------
 * Tiny shared field components for the cards (not exported individually
 * elsewhere — set cards are thin and don't need full form library).
 * ------------------------------------------------------------------ */

export function LabeledRow({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div>
      <label className="block">
        <div className="text-xs font-semibold text-slate-700">{label}</div>
        {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
        <div className="mt-1">{children}</div>
      </label>
    </div>
  );
}

export function ChipSelect({
  options,
  value,
  onChange,
  disabled,
  multi,
}: {
  options: string[];
  value: string | string[] | null | undefined;
  onChange: (v: string | string[]) => void;
  disabled?: boolean;
  multi?: boolean;
}) {
  const isActive = (opt: string) => {
    if (multi) return Array.isArray(value) && value.includes(opt);
    return value === opt;
  };
  const onTap = (opt: string) => {
    if (multi) {
      const cur = (Array.isArray(value) ? value : []) as string[];
      if (cur.includes(opt)) onChange(cur.filter((v) => v !== opt));
      else onChange([...cur, opt]);
    } else {
      onChange(value === opt ? '' : opt);
    }
  };
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          disabled={disabled}
          onClick={() => onTap(opt)}
          className={cn(
            'rounded-md border px-3 py-1.5 text-sm min-h-touch transition-colors',
            isActive(opt)
              ? 'bg-blue-600 border-blue-700 text-white'
              : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50',
          )}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}
